import { addDays, DateOnly } from '@if/core/date'
import { EPaymentMethod } from '@src/types/constants'

/**
 * Computes the earliest planned date a PPA payment can use, mirroring the backend
 * BaseCalendarAction.GetFirstSafeAcpFileDate.
 *
 * Rule: before noon ET on a working day, today is the ACP-locked limit. After noon
 * ET, on a weekend, advance to the next working day. The earliest valid date is
 * limit + 1 calendar day. Users with the canEditLockedPpaPayments right bypass.
 *
 * Note: this mirror is weekend-aware but NOT iFinance-holiday-aware (unlike the
 * backend, which uses Iceberg.Common HolidayCalendar). On a holiday, the BE may
 * reject a date the FE allowed; the user sees the snackbar and corrects.
 */
export class SafePaymentActionDateService {
  private static instance: SafePaymentActionDateService
  private cache: Map<string, DateOnly>
  private todayKey: string

  // Business rule: Once noon is reached, there is no modifications on calendar allowed.
  private static MAX_HOUR_EST_ALLOWED_BEFORE_CALENDAR_MODIFICATIONS = 12

  private constructor() {
    this.cache = new Map<string, DateOnly>()
    this.todayKey = SafePaymentActionDateService.computeEasternDayKey()
  }

  public static getInstance(): SafePaymentActionDateService {
    if (!SafePaymentActionDateService.instance) {
      SafePaymentActionDateService.instance = new SafePaymentActionDateService()
    }
    return SafePaymentActionDateService.instance
  }

  /**
   * @param paymentMethod target payment method
   * @param canBypassAcpLock pass true when the current user has the canEditLockedPpaPayments right
   */
  public getFirstSafeDate(paymentMethod: EPaymentMethod, canBypassAcpLock = false): DateOnly {
    const currentKey = SafePaymentActionDateService.computeEasternDayKey()
    if (this.todayKey !== currentKey) {
      this.cache.clear()
      this.todayKey = currentKey
    }

    const cacheKey = `${paymentMethod}|${canBypassAcpLock}`
    const cached = this.cache.get(cacheKey)
    if (cached) return cached

    const todayEt = SafePaymentActionDateService.todayInEt()
    let safeDate = todayEt
    if (paymentMethod === EPaymentMethod.PreAuthorizedPayment && !canBypassAcpLock) {
      const limit = SafePaymentActionDateService.computeAcpFileLimitDate()
      safeDate = addDays(limit, 1)
    }

    this.cache.set(cacheKey, safeDate)
    return safeDate
  }

  /**
   * Returns the minimum date a payment is allowed to use: the larger of loanActivationDate
   * and the ACP-safe date for PPA, or just loanActivationDate for other methods.
   */
  public getMinPaymentDate(
    paymentMethod: EPaymentMethod,
    loanActivationDate: DateOnly,
    canBypassAcpLock = false,
  ): DateOnly {
    if (paymentMethod !== EPaymentMethod.PreAuthorizedPayment) return loanActivationDate
    const safeAcpDate = this.getFirstSafeDate(paymentMethod, canBypassAcpLock)
    return safeAcpDate > loanActivationDate ? safeAcpDate : loanActivationDate
  }

  /** Last Eastern date that is ACP-locked (mirrors backend GetAcpFileLimitDate, sans holidays). */
  private static computeAcpFileLimitDate(): DateOnly {
    const parts = SafePaymentActionDateService.easternParts()
    const today = DateOnly.fromComponents(parts.year, parts.month - 1, parts.day)
    const noonReached = parts.hour >= SafePaymentActionDateService.MAX_HOUR_EST_ALLOWED_BEFORE_CALENDAR_MODIFICATIONS
    const dow = today.getDay() // 0 = Sun, 6 = Sat
    const isWeekend = dow === 0 || dow === 6

    if (!noonReached && !isWeekend) return today

    if (dow === 5) return addDays(today, 3) // Fri pm → Mon
    if (dow === 6) return addDays(today, 2) // Sat → Mon
    if (dow === 0) return addDays(today, 1) // Sun → Mon
    return addDays(today, 1) // weekday pm → next day
  }

  private static todayInEt(): DateOnly {
    const parts = SafePaymentActionDateService.easternParts()
    return DateOnly.fromComponents(parts.year, parts.month - 1, parts.day)
  }

  private static easternParts(): { year: number; month: number; day: number; hour: number } {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      hour12: false,
    })
    const parts = formatter.formatToParts(new Date())
    const lookup = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0')
    return {
      year: lookup('year'),
      month: lookup('month'),
      day: lookup('day'),
      hour: lookup('hour'),
    }
  }

  private static computeEasternDayKey(): string {
    const parts = SafePaymentActionDateService.easternParts()
    return `${parts.year}-${parts.month}-${parts.day}`
  }
}
